import classnames from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Small } from 'palette/Typography';
import type { TLabelType, ListItemVerticalAlignment } from '../types';
import { ListItemLabel } from './ListItemLabel';
import '../styles/ListItem.scss';

interface ListItemCommonProps {
    className?: string;
    href?: string;
    isClientSideRoute?: boolean;
    onClick?: React.MouseEventHandler;
    leftIcon?: React.ReactElement;
    rightContent?: React.ReactNode;
    secondaryText?: string;
    captionMessage?: string | React.ReactNode;
    isActive?: boolean;
    isUnpadded?: boolean;
    verticalAlignment?: ListItemVerticalAlignment;
}

interface ListItemWithChildren extends ListItemCommonProps {
    children: React.ReactNode;
    label?: never;
    labelType?: never;
}

interface ListItemWithLabel extends ListItemCommonProps {
    children?: never;
    label: string;
    labelType?: TLabelType;
}

type ListItemProps = ListItemWithLabel | ListItemWithChildren;

const getWrapperComponent = (href: any, isClientSideRoute: any) => {
    if (href && isClientSideRoute) {
        return Link;
    }

    return href ? 'a' : 'div';
};

export function ListItem({
    className,
    children,
    href,
    isClientSideRoute,
    onClick,
    leftIcon,
    rightContent,
    label,
    labelType,
    secondaryText,
    captionMessage,
    verticalAlignment,
    isUnpadded,
    isActive = false,
}: ListItemProps) {
    const Component = getWrapperComponent(href, isClientSideRoute);
    const isClickable = typeof onClick !== 'undefined' || href;
    const dynamicAttributes: { to?: string; href?: string } = {};

    if (isClientSideRoute) {
        dynamicAttributes.to = href;
    } else if (href) {
        dynamicAttributes.href = href;
    }

    const clickHandler = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        onClick?.(event);
    };

    return (
        <Component
            className={classnames('palette-list-item', className, {
                'palette-list-item--unpadded': isUnpadded,
                'palette-list-item--clickable': isClickable,
                'palette-list-item--active': isActive,
                [`palette-list-item--v-alignment-${verticalAlignment}`]: verticalAlignment,
            })}
            href={dynamicAttributes.href}
            to={dynamicAttributes.to as string}
            onClick={clickHandler}
        >
            {leftIcon && (
                React.cloneElement(leftIcon, {
                    className: 'palette-list-item__left-icon',
                })
            )}
            <ListItemLabel
                label={label}
                labelType={labelType}
                secondaryText={secondaryText}
            >
                {children}
            </ListItemLabel>
            {!!captionMessage && (
                <Small
                    className="palette-list-item__caption"
                    color="light"
                >
                    {captionMessage}
                </Small>
            )}
            {rightContent && (rightContent)}
        </Component>
    );
}
